/**
 * FASE 4.2 — HomePostLoginDashboard
 * Sezione widget personalizzati mostrata sotto l'hero della homepage SOLO
 * quando l'utente è loggato. 4 card cliccabili + timeline scadenze 90gg
 * + 6 quick-quote pre-compilate.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Briefcase, FolderOpen, Bell, ArrowRight, Clock, Sparkles } from "lucide-react";
import axios from "axios";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { useAuthModals } from "@/components/auth/AuthModalsHost";
import PushOfferBanner from "@/components/auth/PushOfferBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/client`;

export default function HomePostLoginDashboard() {
  const { account, getToken } = useClientAuth();
  const { openAreaCliente } = useAuthModals();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!account) return;
    const token = getToken();
    if (!token) return;
    const h = { headers: { Authorization: `Bearer ${token}` } };
    setLoading(true);
    Promise.all([
      axios.get(`${API}/policies`, h).catch(() => ({ data: { items: [] } })),
      axios.get(`${API}/quote-requests`, h).catch(() => ({ data: { items: [] } })),
      axios.get(`${API}/documents`, h).catch(() => ({ data: { items: [] } })),
      axios.get(`${API}/notifications`, h).catch(() => ({ data: { items: [] } })),
    ]).then(([p, q, d, n]) => {
      setData({
        policies: p.data?.items || [],
        quotes: q.data?.items || [],
        documents: d.data?.items || [],
        notifications: n.data?.items || [],
      });
    }).finally(() => setLoading(false));
  }, [account, getToken]);

  if (!account) return null;

  const name = account.profilo_titolare?.anagrafica?.nome
    || account.email?.split("@")[0]
    || "Cliente";

  const polActive = (data?.policies || []).filter(p => (p.stato || "").toLowerCase() === "attiva");
  const nextSca = polActive
    .filter(p => p.data_scadenza)
    .sort((a, b) => a.data_scadenza.localeCompare(b.data_scadenza))[0];
  const unreadNotif = (data?.notifications || []).filter(n => !n.read_at);

  const widgets = [
    {
      key: "polizze", title: "Le mie polizze", icon: FileText,
      count: polActive.length,
      hint: nextSca ? `Prossima scadenza: ${nextSca.data_scadenza}` : "Nessuna attiva",
      cta: "Vedi tutte", color: "emerald",
    },
    {
      key: "preventivi", title: "I miei preventivi", icon: Briefcase,
      count: (data?.quotes || []).length,
      hint: data?.quotes?.[0]?.tipo_richiesta ? `Ultimo: ${data.quotes[0].tipo_richiesta}` : "Nessuno in corso",
      cta: "Vedi tutti", color: "blue",
    },
    {
      key: "documenti", title: "I miei documenti", icon: FolderOpen,
      count: (data?.documents || []).length,
      hint: "Carica e gestisci i tuoi file",
      cta: "Gestisci", color: "violet",
    },
    {
      key: "notifiche", title: "Notifiche", icon: Bell,
      count: unreadNotif.length, badge: unreadNotif.length,
      hint: unreadNotif[0]?.titolo || "Tutto in regola",
      cta: "Vedi tutte", color: "amber",
    },
  ];

  const colorMap = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
  };

  const quickQuotes = [
    { label: "Auto", href: "/preventivo-auto", emoji: "🚗" },
    { label: "Casa", href: "/questionario/casa", emoji: "🏠" },
    { label: "Vita", href: "/questionario/vita", emoji: "💼" },
    { label: "Salute", href: "/questionario/sanitario", emoji: "❤️" },
    { label: "Viaggio", href: "/contatti?prodotto=viaggio", emoji: "✈️" },
    { label: "Azienda", href: "/questionario/aziende", emoji: "🏢" },
    { label: "Noleggio", href: "/noleggio?tipo_cliente=privati", emoji: "🚙" },
  ];

  return (
    <section className="bg-gradient-to-b from-emerald-50/30 to-white border-b border-zinc-100 py-12"
      data-testid="home-post-login-dashboard">
      <div className="max-w-7xl mx-auto px-6">
        <PushOfferBanner />
        <header className="flex items-end justify-between mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
              Bentornato/a, {name}
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-[#163321]">La tua dashboard</h2>
          </div>
          <button onClick={() => openAreaCliente("dashboard")}
            className="hidden md:flex items-center gap-1.5 text-sm text-emerald-700 hover:underline"
            data-testid="dashboard-open-full">
            Apri panoramica completa <ArrowRight className="w-4 h-4" />
          </button>
        </header>

        {/* 4 widget card */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {widgets.map((w) => {
            const Icon = w.icon;
            return (
              <button key={w.key} onClick={() => openAreaCliente(w.key)}
                className="bg-white rounded-2xl p-4 border border-zinc-200 text-left hover:border-emerald-300 hover:shadow-md transition group"
                data-testid={`dashboard-widget-${w.key}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${colorMap[w.color]}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  {w.badge > 0 && (
                    <span className="bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                      {w.badge}
                    </span>
                  )}
                </div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider font-medium">{w.title}</p>
                <p className="text-3xl font-bold text-[#163321] mt-1">{loading ? "…" : w.count}</p>
                <p className="text-[11px] text-zinc-500 mt-1 truncate">{w.hint}</p>
                <p className="text-[11px] text-emerald-700 mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">
                  {w.cta} <ArrowRight className="w-3 h-3" />
                </p>
              </button>
            );
          })}
        </div>

        {/* Prossime scadenze timeline */}
        {polActive.length > 0 && (
          <div className="bg-white rounded-2xl p-5 border border-zinc-200 mb-6" data-testid="dashboard-timeline">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-amber-600" />
              <h3 className="text-sm font-semibold text-[#163321]">Le prossime scadenze</h3>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {polActive
                .filter(p => p.data_scadenza)
                .sort((a, b) => a.data_scadenza.localeCompare(b.data_scadenza))
                .slice(0, 6)
                .map((p) => (
                  <div key={p.id} className="min-w-[180px] bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-[10px] text-amber-700 uppercase tracking-wider font-medium">{p.ramo}</p>
                    <p className="text-sm font-medium text-zinc-900 mt-0.5 truncate">{p.numero_polizza || "—"}</p>
                    <p className="text-xs text-amber-800 mt-1">Scade: {p.data_scadenza}</p>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Quick quote pre-compilate */}
        <div className="bg-white rounded-2xl p-5 border border-zinc-200" data-testid="dashboard-quick-quotes">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-[#163321]">Quotazioni rapide pre-compilate</h3>
            <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">30 secondi</span>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {quickQuotes.map((qq) => (
              <Link key={qq.label} to={qq.href}
                className="flex flex-col items-center gap-1 bg-zinc-50 hover:bg-emerald-50 border border-zinc-200 hover:border-emerald-300 rounded-xl px-2 py-3 text-center transition group"
                data-testid={`quick-quote-${qq.label.toLowerCase()}`}>
                <span className="text-2xl">{qq.emoji}</span>
                <span className="text-xs font-medium text-zinc-700 group-hover:text-emerald-700">{qq.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
